import React, { useEffect, useState, useRef } from "react";
import { useDropzone } from "react-dropzone";
import "bootstrap/dist/css/bootstrap.min.css";
import "./createService.css";
import axiosInstance from "../../API/axiosInstance";
import { fetchCategories } from "../../API/Resources/fetchCategories";
import { fetchStates } from "../../API/Resources/fetchStates";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer, Bounce } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Modal, Popover } from "bootstrap";
import { fetchCities } from "../../API/Resources/fetchCities";
import { BsInfoCircleFill } from "react-icons/bs";

const ServiceForm = () => {
    const navigator = useNavigate();
    const [serviceData, setServiceData] = useState({
        service_name: "",
        description: "",
        category: "",
        min_price: "",
        max_price: "",
        state: "",
        location: "",
        files: [],
    });

    const [isDisabled, setIsDisabled] = useState(false);
    const [vendorId, setVendorId] = useState();
    const [errors, setErrors] = useState({});
    const [token, setToken] = useState();
    const [categories, setCategories] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [currentUser, setCurrentUser] = useState(() => {
        const storedUser = localStorage.getItem("currentUser")
        if (storedUser)
            return JSON.parse(storedUser);
        else
            return null;
    })
    const popoverRef = useRef();

    useEffect(() => {
        let popover; 

        if (popoverRef.current) {
            popover = new Popover(popoverRef.current, {
                title: 'Premium Feature',
                html: true,
                content: `Changing your category requires a premium plan. <a href="/pricing" class="btn btn-primary btn-sm d-block mt-2">Upgrade Now</a>`,
                trigger: 'hover focus',
                placement: 'top',
            });
        }

        return () => {
            if (popover) {
                popover.dispose();
            }
        };
    }, []);

    const locationPopoverRef = useRef();
    useEffect(() => {
        if (locationPopoverRef.current) {
            const popover = new Popover(locationPopoverRef.current, {
                title: 'Premium Feature',
                html: true,
                content: 'Your location is fixed to your profile. Upgrade your plan to offer services in other areas. <a href="/pricing" class="btn btn-primary btn-sm d-block mt-2">Upgrade Now</a>',
                trigger: 'hover focus',
                placement: 'top',
            });


            return () => {
                popover.dispose();
            };
        }
    }, []);

    const modalId = "membershipModal";

    useEffect(() => {
        if (showModal) {
            const modalElement = document.getElementById(modalId);
            if (modalElement) {
                const modalInstance = new Modal(modalElement);
                modalInstance.show();
            }
        }
    }, [showModal]);

    const notify = (status, message) => {
        const options = {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "dark",
            transition: Bounce,
        };
        if (status === "success") {
            toast.success(message, options);
        } else {
            toast.error(message, options);
        }
    };

    useEffect(() => {
        const storedUser = JSON.parse(localStorage.getItem("currentUser"));
        const storedToken = localStorage.getItem("token");

        if (storedUser) setVendorId(storedUser.id);
        if (storedToken) setToken(storedToken);

        const getCategories = async () => {
            const categoryList = await fetchCategories();
            setCategories(categoryList);
        };

        const getStates = async () => {
            const stateList = await fetchStates();
            setStates(stateList);
        };

        getCategories();
        getStates();
    }, []);
    useEffect(() => {
        const getCities = async () => {
            if (serviceData.state && currentUser && currentUser.planType !== 'BASIC') {
                setServiceData(prev => ({ ...prev, location: "" }));
                const cityList = await fetchCities(serviceData.state);
                setCities(cityList);
            }
        };
        getCities();
    }, [serviceData.state, currentUser]);

    useEffect(() => {
        const requiredFields = ["service_name", "description", "category", "min_price", "max_price", "state", "location"];
        const allFieldsFilled = requiredFields.every((field) => serviceData[field]?.toString().trim() !== "");
        const noErrors = requiredFields.every((field) => !errors[field]);
        const hasFiles = serviceData.files.length > 0;

        setIsDisabled(allFieldsFilled && noErrors && hasFiles);
    }, [serviceData, errors]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setServiceData((prevData) => ({ ...prevData, [name]: value }));
        validateField(name, value);
    };

    const validateField = (name, value) => {
        let errorMsg = "";
        if (typeof value === 'string' && !value.trim()) {
            errorMsg = "This field is required.";
        } else if (name === "description") {
            const minWords = 100;
            const wordCount = value.trim().split(/\s+/).filter(Boolean).length;

            if (wordCount < minWords) {
                errorMsg = `Description must be at least ${minWords} words long.`;
            }
        } else if (name === "min_price" || name === "max_price") {
            if (isNaN(value) || Number(value) < 0) {
                errorMsg = "Price must be a positive number.";
            } else if (name === "max_price" && Number(value) < Number(serviceData.min_price)) {
                errorMsg = "Max price cannot be less than min price.";
            }
        }
        setErrors((prevErrors) => ({ ...prevErrors, [name]: errorMsg }));
    };

    const handleFileDrop = (acceptedFiles) => {
        setServiceData((prevData) => ({
            ...prevData,
            files: [...prevData.files, ...acceptedFiles.map(file =>
                Object.assign(file, { preview: URL.createObjectURL(file) })
            )]
        }));
    };

    const handleFileRemove = (index) => {
        setServiceData((prevData) => ({
            ...prevData,
            files: prevData.files.filter((_, i) => i !== index)
        }));
    };

    useEffect(() => {
        if (currentUser && currentUser.planType === 'BASIC') {
            setServiceData(prevData => ({
                ...prevData,
                category: currentUser.business_category,
                state: currentUser.state,
                location: currentUser.city
            }));
        }
    }, [currentUser]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        let formIsValid = true;
        Object.keys(serviceData).forEach(key => {
            validateField(key, serviceData[key]);
            if (!serviceData[key] && key !== 'files') {
                formIsValid = false;
            }
        });

        if (serviceData.files.length < 10) {
            notify("error", "Upload at least 10 images.");
            setLoading(false);
            return;
        }

        if (!formIsValid || serviceData.files.length === 0) {
            notify("error", "Please fill all mandatory fields and upload at least one image.");
            setLoading(false);
            return;
        }

        const formData = new FormData();
        Object.entries(serviceData).forEach(([key, value]) => {
            if (key !== "files") {
                formData.append(key, value);
            }
        });

        serviceData.files.forEach((file) => {
            formData.append("files", file);
        });

        try {
            if (!vendorId || !token) {
                notify("error", "Authentication error. Please log in again.");
                return;
            }
            const response = await axiosInstance.post(`/service/${vendorId}/create-service`, formData, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "multipart/form-data",
                }
            });
            if (response.status === 200 || response.status === 201) {
                notify("success", "Service added successfully!");
                setTimeout(() => {
                    navigator("/vendor-dashboard");
                }, 2000);
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.response?.data || "Something went wrong.";
            notify("error", errorMsg);
            if (err.response?.status === 403) {
                setShowModal(true);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container" id="main-div">
            <ToastContainer />
            <div className="card p-4 p-md-5 shadow-lg">
                <h1 className="text-center mb-4" id="title">Create a New Service</h1>
                <p className="text-center text-muted mb-4">Fill out the details below to list your new service.</p>

                <form onSubmit={handleSubmit} noValidate>
                    <div className="mb-4">
                        <label className="form-label">Service Name <span className="text-danger">*</span></label>
                        <input
                            type="text"
                            className={`form-control form-control-lg ${errors.service_name ? "is-invalid" : ""}`}
                            name="service_name"
                            placeholder="e.g., Professional Wedding Photography"
                            value={serviceData.service_name}
                            onChange={handleChange}
                            required
                        />
                        {errors.service_name && <div className="invalid-feedback">{errors.service_name}</div>}
                    </div>

                    <div className="mb-4">
                        <label className="form-label">Description <span className="text-danger">*</span></label>
                        <textarea
                            className={`form-control form-control-lg ${errors.description ? "is-invalid" : ""}`}
                            name="description"
                            placeholder="Describe your service in detail (minimum 50 words)..."
                            value={serviceData.description}
                            onChange={handleChange}
                            rows="4"
                            required
                            minLength="100"
                        ></textarea>
                        <div className="form-text text-end">
                            {serviceData.description.trim().split(/\s+/).filter(Boolean).length} / 100 words
                        </div>

                        {errors.description && <div className="invalid-feedback">{errors.description}</div>}
                    </div>


                    {currentUser.planType === 'BASIC' || currentUser.planType === 'POPULAR' ? (
                        <>
                            <div className="mb-4">
                                <label className="form-label d-flex justify-content-between align-items-center">
                                    <span>Category <span className="text-danger">*</span></span>

                                    {/* Information icon that will trigger the popover */}
                                    <span ref={popoverRef} style={{ cursor: 'pointer' }}>
                                        <BsInfoCircleFill
                                            size={18}
                                            color="#0d6efd"
                                            className="ms-2"
                                            style={{ cursor: 'pointer' }}
                                        />
                                    </span>
                                </label>

                                <input
                                    type="text"
                                    className="form-control form-control-lg"
                                    id="category"
                                    name="category"
                                    value={currentUser.business_category}
                                    disabled
                                    readOnly
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="mb-4">
                                <label className="form-label">Category <span className="text-danger">*</span></label>
                                <select
                                    className={`form-select form-select-lg ${errors.category ? "is-invalid" : ""}`}
                                    name="category"
                                    value={serviceData.category}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="" disabled>Select a category</option>
                                    {categories.map((cat) => (
                                        <option key={cat.id} value={cat.category_name}>
                                            {cat.category_name}
                                        </option>
                                    ))}
                                </select>
                                {errors.category && <div className="invalid-feedback">{errors.category}</div>}
                            </div>
                        </>
                    )}

                    <div className="row mb-3 h-auto">
                        <div className="col-md-6 m-0 p-0">
                            <label className="form-label">Min Price (₹) <span className="text-danger">*</span></label>
                            <input
                                type="number"
                                className={`form-control form-control-lg ${errors.min_price ? "is-invalid" : ""}`}
                                name="min_price"
                                placeholder="e.g., 500"
                                value={serviceData.min_price}
                                onChange={handleChange}
                                required
                            />
                            {errors.min_price && <div className="invalid-feedback">{errors.min_price}</div>}
                        </div>
                        <div className="col-md-6 mt-3 mt-md-0">
                            <label className="form-label">Max Price (₹) <span className="text-danger">*</span></label>
                            <input
                                type="number"
                                className={`form-control form-control-lg ${errors.max_price ? "is-invalid" : ""}`}
                                name="max_price"
                                placeholder="e.g., 2500"
                                value={serviceData.max_price}
                                onChange={handleChange}
                                required
                            />
                            {errors.max_price && <div className="invalid-feedback">{errors.max_price}</div>}
                        </div>
                    </div>

                    {currentUser.planType !== "BASIC" ? (
                        // TRUE Case: For Premium/Popular users (No changes needed here)
                        <div className="row mb-3 h-auto">
                            <div className="row mb-3 h-auto">
                                <div className="col-md-6 m-0 p-0">
                                    <label className="form-label">State <span className="text-danger">*</span></label>
                                    <select
                                        className={`form-select form-select-lg ${errors.state ? "is-invalid" : ""}`}
                                        name="state"
                                        value={serviceData.state}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="" disabled>Select a state</option>
                                        {states.map((st) => (
                                            <option key={st} value={st}>{st}</option>
                                        ))}
                                    </select>
                                    {errors.state && <div className="invalid-feedback">{errors.state}</div>}
                                </div>
                                <div className="col-md-6 mt-3 mt-md-0">
                                    <label className="form-label">City <span className="text-danger">*</span></label>
                                    <select
                                        className={`form-select form-select-lg ${errors.location ? "is-invalid" : ""}`}
                                        name="location"
                                        value={serviceData.location}
                                        onChange={handleChange}
                                        required
                                        disabled={!serviceData.state || cities.length === 0}
                                    >
                                        <option value="" disabled>Select a city</option>
                                        {cities.map((city) => (
                                            <option key={city} value={city}>{city}</option>
                                        ))}
                                    </select>
                                    {errors.location && <div className="invalid-feedback">{errors.location}</div>}
                                </div>
                            </div>
                        </div>
                    ) : (
                        // FALSE Case: For BASIC users - This is the updated part
                        <div className="row mb-3 h-auto">
                            <div className="col-md-6 m-0 p-0">
                                <label className="form-label">State <span className="text-danger">*</span></label>
                                <input
                                    type="text"
                                    name="state"
                                    className="form-control form-control-lg"
                                    value={serviceData.state}
                                    disabled
                                    readOnly
                                />
                            </div>
                            <div className="col-md-6 mt-3 mt-md-0">
                                <label className="form-label d-flex justify-content-between align-items-center">
                                    <span>City <span className="text-danger">*</span></span>
                                    <span ref={locationPopoverRef} style={{ cursor: 'pointer' }}>
                                        <BsInfoCircleFill size={18} color="#0d6efd" />
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    name="location"
                                    className="form-control form-control-lg"
                                    value={serviceData.location}
                                    disabled
                                    readOnly
                                />
                            </div>
                        </div>
                    )}

                    <div className="mb-4">
                        <label className="form-label">
                            Service Images & Documents <span className="text-danger">*</span>
                        </label>
                        <div className="form-text text-muted mt-0 mb-2">
                            Note: A minimum of 10 photos are required.
                        </div>
                        <FileUpload
                            files={serviceData.files}
                            onDrop={handleFileDrop}
                            onRemove={handleFileRemove}
                        />
                    </div>
                    <button
                        type="submit"
                        className="btn create-service-btn w-100"
                        disabled={!isDisabled || loading}
                    >
                        {loading ? <span className="spinner-border spinner-border-sm"></span> : "Create Service"}
                    </button>
                </form>
            </div>
        </div>
    );
};


const FileUpload = ({ files, onDrop, onRemove }) => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: {
            'image/jpeg': ['.jpeg', '.jpg'],
            'image/png': ['.png'],
            'application/pdf': ['.pdf']
        },
        multiple: true,
        onDrop,
    });

    return (
        <div className="file-upload-container">
            <div {...getRootProps()} className={`dropzone-area ${isDragActive ? "active" : ""}`}>
                <input {...getInputProps()} />
                <div className="upload-icon">
                    {/* SVG for a file upload icon - more generic */}
                    <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" fill="currentColor" className="bi bi-cloud-arrow-up" viewBox="0 0 16 16">
                        <path fillRule="evenodd" d="M7.646 5.146a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1-.708.708L8.5 6.707V10.5a.5.5 0 0 1-1 0V6.707L6.354 7.854a.5.5 0 1 1-.708-.708l2-2z" />
                        <path d="M4.406 3.342A5.53 5.53 0 0 1 8 2c2.69 0 4.923 2 5.166 4.579C14.758 6.804 16 8.3 16 9.773 16 11.569 14.502 13 12.687 13H3.781C1.708 13 0 11.366 0 9.318c0-1.763 1.266-3.223 3.138-3.563 1.06-.339 2.18-.763 3.092-1.878zM4.17 5.038a.5.5 0 0 1 .724-.04L7.5 7.08V3.5a.5.5 0 0 1 1 0v3.58l2.596-2.081a.5.5 0 1 1 .644.768L8.5 9.066a.5.5 0 0 1-.644 0L4.246 5.766a.5.5 0 0 1-.076-.728z" />
                    </svg>
                </div>
                <p className="dropzone-text">
                    {isDragActive ? "Drop the files here..." : "Drag & drop files here, or click to select"}
                </p>
                <p className="dropzone-hint">Accepted: JPEG, PNG, PDF</p>
            </div>

            {files.length > 0 && (
                <div className="file-preview-grid">
                    {files.map((file, index) => (
                        <div key={index} className="preview-item">
                            <img
                                src={file.type.startsWith("image/") ? file.preview : '/images/pdf-icon.png'} // Assumes you have a pdf-icon.png in your public/images folder
                                alt={file.name}
                                className="preview-image"
                                onLoad={() => URL.revokeObjectURL(file.preview)}
                            />
                            <span className="file-name">{file.name}</span>
                            <button type="button" className="remove-file-btn" onClick={() => onRemove(index)}>
                                &times;
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ServiceForm;